/** 展立超频 Arena：变化时关闭动画，稳定后再按新布局出现，避免实时跟随卡顿 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./ZhanliOverclockArena.scss";

type ArenaFrame = {
  width: number;
  height: number;
  padTop: number;
  hubX: number;
  hubY: number;
};

interface ZhanliOverclockArenaProps {
  active: boolean;
  /** 当前轮询率 Hz（用于艺术字展示） */
  reportRateHz?: number;
}

type ArenaStatItem = {
  id: string;
  label: string;
  value: string;
  maxed?: boolean;
  /** MAX 在文案前（右侧）；默认文案后（左侧） */
  maxFirst?: boolean;
  accent?: boolean;
  /** 数值后缀，如 FPS / 22h */
  accentSuffix?: string;
};

const MOUSE_CONTENT_SELECTOR = ".mouse-content";
const MOUSE_HUB_SELECTOR =
  ".performance-left--zhanli .mouse-model, .performance-left--zhanli .mouse-display";
const MOUSE_LAYOUT_SELECTOR =
  ".performance-left--zhanli, .performance-left--zhanli .mouse-display, .performance-left--zhanli .mouse-model";

function getMaxEffectRadius() {
  return 340;
}

/** 圆环扩散：区域 680、目标径向间距 40px
 * 无缝条件：duration === count * delay（首尾衔接下一圈）
 * 680/80=8.5 非整数，取 9 圈后实际间距 ≈37.8px，优先保证循环闭合
 */
const RING_AREA = 680;
const RING_GAP = 40;
const RING_COUNT = Math.round(RING_AREA / (RING_GAP * 2));
const RING_DURATION_S = 16;
const RING_DELAY_S = RING_DURATION_S / RING_COUNT;

const PAD_BOTTOM = 64;
const PAD_MARGIN = 56;
/** 安静多久后认为布局停稳（ms） */
const SETTLE_IDLE_MS = 220;
/** 连续稳定采样帧数 */
const STABLE_FRAME_COUNT = 4;
/** 开启后最长等待（含右侧面板 0.4s transition） */
const SETTLE_MAX_MS = 720;

const OVERFLOW_CHAIN_SELECTORS = [
  "#subapp-container",
  "#__qiankun_microapp_wrapper_for_mouse_management__",
  ".layout-main",
  ".layout-body",
];

function setOverflowChainVisible(visible: boolean) {
  for (const selector of OVERFLOW_CHAIN_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) continue;

    if (visible) {
      if (el.dataset.overclockOverflow === undefined) {
        el.dataset.overclockOverflow = el.style.overflow || "";
      }
      el.style.overflow = "visible";
    } else if (el.dataset.overclockOverflow !== undefined) {
      el.style.overflow = el.dataset.overclockOverflow;
      delete el.dataset.overclockOverflow;
    }
  }
}

function frameStableKey(frame: ArenaFrame) {
  return `${Math.round(frame.hubX)}:${Math.round(frame.hubY)}:${Math.round(frame.width)}:${Math.round(frame.height)}`;
}

function readArenaFrame(): ArenaFrame | null {
  const content = document.querySelector<HTMLElement>(MOUSE_CONTENT_SELECTOR);
  const mouse = document.querySelector<HTMLElement>(MOUSE_HUB_SELECTOR);
  if (!content || !mouse) return null;

  const contentRect = content.getBoundingClientRect();
  const mouseRect = mouse.getBoundingClientRect();
  const hubYInContent = mouseRect.top + mouseRect.height / 2 - contentRect.top;

  const padTop = Math.ceil(
    Math.max(160, getMaxEffectRadius() - hubYInContent + PAD_MARGIN)
  );

  return {
    width: contentRect.width,
    height: contentRect.height + padTop + PAD_BOTTOM,
    padTop,
    hubX: mouseRect.left + mouseRect.width / 2 - contentRect.left,
    hubY: hubYInContent + padTop,
  };
}

function formatReportRateValue(hz: number): string {
  if (hz >= 1000) {
    const k = hz / 1000;
    return Number.isInteger(k) ? `${k}KHz` : `${k.toFixed(1)}KHz`;
  }
  return `${hz}Hz`;
}

const ZhanliOverclockArena: React.FC<ZhanliOverclockArenaProps> = ({
  active,
  reportRateHz = 8000,
}) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(active);
  const [mountEl, setMountEl] = useState<HTMLElement | null>(null);
  const [frame, setFrame] = useState<ArenaFrame | null>(null);
  const [visible, setVisible] = useState(false);
  const activeRef = useRef(active);
  /** 布局变化中：暂停动画，保留 DOM */
  const movingRef = useRef(false);
  const restartEnterRef = useRef<(() => void) | null>(null);

  activeRef.current = active;

  const leftStats: ArenaStatItem[] = [
    {
      id: "report-rate",
      label: t("mouse.performance.reportRate"),
      value: formatReportRateValue(reportRateHz),
      maxed: true,
    },
    {
      id: "perf-mode",
      label: t("mouse.performance.zhanliPerformanceMode"),
      value: t("mouse.performance.fps20kOverclockMode"),
      maxed: true,
      accent: true,
    },
  ];

  const rightStats: ArenaStatItem[] = [
    {
      id: "scan-20k",
      label: t("mouse.performance.fps20kArenaScanLabel"),
      value: "20K ",
      accentSuffix: "FPS",
      maxed: true,
      maxFirst: true,
    },
    {
      id: "battery",
      label: t("mouse.performance.batteryLife"),
      value: "20~",
      accentSuffix: "22h",
    },
  ];

  useEffect(() => {
    if (active) {
      setMounted(true);
      restartEnterRef.current?.();
      return;
    }

    setMounted(false);
  }, [active]);

  useLayoutEffect(() => {
    if (!mounted) return;

    const content = document.querySelector<HTMLElement>(MOUSE_CONTENT_SELECTOR);
    const mouseBox = document.querySelector<HTMLElement>(".mouse-box");
    if (!content) return;

    content.classList.add("mouse-content--overclock");
    mouseBox?.classList.add("mouse-box--overclock");

    const slot = document.createElement("div");
    slot.className = "zhanli-overclock-arena-mount";
    content.insertBefore(slot, content.firstChild);
    setMountEl(slot);

    let settleRaf = 0;
    let coalesceRaf = 0;
    let idleTimer = 0;
    let maxTimer = 0;

    const showEffectsWithFrame = (next: ArenaFrame) => {
      setFrame(next);
      setOverflowChainVisible(true);
      requestAnimationFrame(() => {
        if (activeRef.current && !movingRef.current) {
          setVisible(true);
        }
      });
    };

    const beginMoving = () => {
      if (!activeRef.current) return;
      movingRef.current = true;
      setVisible(false);
      const next = readArenaFrame();
      if (next) setFrame(next);
    };

    const trySettle = () => {
      if (!activeRef.current || !movingRef.current) return;
      cancelAnimationFrame(settleRaf);

      let lastKey = "";
      let stable = 0;

      const tick = () => {
        if (!activeRef.current || !movingRef.current) return;

        const next = readArenaFrame();
        const key = next ? frameStableKey(next) : "";

        if (key && key === lastKey) {
          stable += 1;
          if (stable >= STABLE_FRAME_COUNT && next) {
            movingRef.current = false;
            showEffectsWithFrame(next);
            return;
          }
        } else {
          lastKey = key;
          stable = 0;
        }

        settleRaf = requestAnimationFrame(tick);
      };

      settleRaf = requestAnimationFrame(tick);
    };

    const onLayoutChange = () => {
      if (!activeRef.current) return;
      beginMoving();

      window.clearTimeout(idleTimer);
      cancelAnimationFrame(settleRaf);

      idleTimer = window.setTimeout(() => {
        if (!activeRef.current) return;
        trySettle();
      }, SETTLE_IDLE_MS);
    };

    const coalesceLayoutChange = () => {
      cancelAnimationFrame(coalesceRaf);
      coalesceRaf = requestAnimationFrame(onLayoutChange);
    };

    const onLayoutTransitionEnd = (event: TransitionEvent) => {
      if (!activeRef.current) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches(MOUSE_LAYOUT_SELECTOR)) return;
      if (
        event.propertyName !== "width" &&
        event.propertyName !== "height" &&
        event.propertyName !== "transform" &&
        event.propertyName !== "padding"
      ) {
        return;
      }
      onLayoutChange();
    };

    const startCycle = () => {
      if (!activeRef.current) return;
      beginMoving();
      window.clearTimeout(maxTimer);
      window.clearTimeout(idleTimer);
      cancelAnimationFrame(settleRaf);

      idleTimer = window.setTimeout(() => {
        if (!activeRef.current) return;
        trySettle();
      }, SETTLE_IDLE_MS);

      maxTimer = window.setTimeout(() => {
        if (!activeRef.current || !movingRef.current) return;
        cancelAnimationFrame(settleRaf);
        const next = readArenaFrame();
        if (next) {
          movingRef.current = false;
          showEffectsWithFrame(next);
        }
      }, SETTLE_MAX_MS);
    };

    restartEnterRef.current = startCycle;

    if (activeRef.current) {
      startCycle();
    }

    const ro = new ResizeObserver(coalesceLayoutChange);
    ro.observe(content);
    document.querySelectorAll(MOUSE_LAYOUT_SELECTOR).forEach((el) => {
      ro.observe(el);
    });
    window.addEventListener("resize", coalesceLayoutChange);
    document.addEventListener("transitionend", onLayoutTransitionEnd);

    return () => {
      content.classList.remove("mouse-content--overclock");
      mouseBox?.classList.remove("mouse-box--overclock");
      setOverflowChainVisible(false);
      window.clearTimeout(idleTimer);
      window.clearTimeout(maxTimer);
      cancelAnimationFrame(coalesceRaf);
      cancelAnimationFrame(settleRaf);
      ro.disconnect();
      window.removeEventListener("resize", coalesceLayoutChange);
      document.removeEventListener("transitionend", onLayoutTransitionEnd);
      slot.remove();
      restartEnterRef.current = null;
      movingRef.current = false;
      setMountEl(null);
      setFrame(null);
      setVisible(false);
    };
  }, [mounted]);

  if (!mounted || !mountEl || !frame) return null;

  const stateClass = visible
    ? " zhanli-overclock-arena--visible"
    : " zhanli-overclock-arena--pending";

  const renderStat = (
    stat: ArenaStatItem,
    index: number,
    side: "left" | "right"
  ) => {
    const maxTag = stat.maxed ? (
      <span className="zhanli-overclock-arena__stat-max">MAX</span>
    ) : null;

    // 渐变用 inline：生产 experimentalStyleIsolation 会弄丢 stylesheet 里的 background
    const valueGradientStyle: React.CSSProperties = {
      backgroundImage:
        side === "right"
          ? "linear-gradient(-76deg, #ff73c4 0%, var(--text-primary) 68%)"
          : "linear-gradient(104deg, #ff73c4 0%, var(--text-primary) 68%)",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: "transparent",
      color: "transparent",
    };

    return (
      <div
        key={stat.id}
        className={`zhanli-overclock-arena__stat${stat.accent ? " zhanli-overclock-arena__stat--accent" : ""}`}
        style={{ ["--stat-i" as string]: index }}
      >
        <div className="zhanli-overclock-arena__stat-head">
          {stat.maxFirst ? maxTag : null}
          <span className="zhanli-overclock-arena__stat-label">
            {stat.label}
          </span>
          {!stat.maxFirst ? maxTag : null}
        </div>
        <span
          className="zhanli-overclock-arena__stat-value"
          style={valueGradientStyle}
        >
          {stat.value}
          {stat.accentSuffix ? (
            <span className="zhanli-overclock-arena__stat-value-accent">
              {stat.accentSuffix}
            </span>
          ) : null}
        </span>
      </div>
    );
  };

  const node = (
    <div
      className={`zhanli-overclock-arena${stateClass}`}
      style={{
        width: frame.width,
        height: frame.height,
        top: -frame.padTop,
        ["--overclock-hub-x" as string]: `${frame.hubX}px`,
        ["--overclock-hub-y" as string]: `${frame.hubY}px`,
      }}
      aria-hidden
    >
      <div className="zhanli-overclock-arena__hub">
        <div className="zhanli-overclock-arena__rings" aria-hidden>
          {Array.from({ length: RING_COUNT }, (_, i) => (
            <div
              key={i}
              className="zhanli-overclock-arena__ring"
              style={{
                ["--ring-i" as string]: i,
                // inline 动画：避免生产 isolation 下 animation + var() 变成 none
                animation: `zhanli-overclock-ring-spread ${RING_DURATION_S}s linear infinite`,
                animationDelay: `${(-i * RING_DELAY_S).toFixed(4)}s`,
              }}
            />
          ))}
        </div>
        <div className="zhanli-overclock-arena__mist" aria-hidden />
      </div>
      <div className="zhanli-overclock-arena__stats zhanli-overclock-arena__stats--left">
        {leftStats.map((stat, index) => renderStat(stat, index, "left"))}
      </div>
      <div className="zhanli-overclock-arena__stats zhanli-overclock-arena__stats--right">
        {rightStats.map((stat, index) => renderStat(stat, index, "right"))}
      </div>
    </div>
  );

  return createPortal(node, mountEl);
};

export default ZhanliOverclockArena;
